/**
 * /earthworks/sites/new — add a site (POST /earthworks/sites). A full page,
 * not a modal. On save the new site's own page replaces this one, so Back
 * lands on the register. Admin / office staff only.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateEwSite, useEwCustomers, useEwMeta } from '../../hooks/useEarthworks';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminEw, ewRefusal } from '../../lib/earthworks';
import { useBackToList } from '../downhole/recordPage';
import { EwSiteForm } from './SiteForm';
import { EW_EYEBROW, EwEmpty, EwLoading, EwPanel } from './EwBits';

export default function EwNewSiteScreen() {
  const navigate = useNavigate();
  const back = useBackToList('/earthworks/sites');
  const { user } = useAuth();
  const canAdd = canAdminEw(user?.role);
  const meta = useEwMeta();
  const customers = useEwCustomers(canAdd);
  const create = useCreateEwSite();
  const [error, setError] = useState<string | null>(null);
  const metaFailure = queryLoadFailure(meta);
  const customersFailure = queryLoadFailure(customers);

  return (
    <div data-testid="ew-new-site-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={EW_EYEBROW} title="New site" onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <EwPanel eyebrow="Site register" title="Site">
            {!canAdd ? (
              <EwEmpty>Only an admin or office staff can add a site.</EwEmpty>
            ) : meta.isLoading || customers.isLoading ? (
              <EwLoading label="Loading form" />
            ) : metaFailure || customersFailure ? (
              <LoadFailureNotice
                error={queryFailureError(metaFailure ? meta : customers)}
                kind={(metaFailure ?? customersFailure) || 'unreachable'}
                subject={metaFailure ? 'the site form' : 'your customers'}
              />
            ) : (customers.data ?? []).length === 0 ? (
              <EwEmpty>A site belongs to a customer, and there are no customers yet. Add one under Customers first.</EwEmpty>
            ) : (
              <EwSiteForm
                site={null}
                catalog={meta.data?.data ?? null}
                meta={meta.data?.meta ?? null}
                customers={customers.data ?? []}
                pending={create.isPending}
                error={error}
                submitLabel="Create site"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success('Site created');
                      if (res.data?.id) navigate(`/earthworks/sites/${res.data.id}`, { replace: true });
                      else navigate('/earthworks/sites', { replace: true });
                    },
                    onError: (err) => setError(ewRefusal(err, 'The site could not be created.')),
                  });
                }}
              />
            )}
          </EwPanel>
        </div>
      </section>
    </div>
  );
}
